import { Box, Checkbox, Dialog, FormControlLabel, Typography } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import FilterAltIcon from '@mui/icons-material/FilterAlt';

import { EStatusLinkManagement } from '@organisms/Tables/Enterprise/LinksManagementTable/enums';
import { StatusLinkManagementValueMapper } from '../constants';

interface IProps {
  paramKey: 'status';
}

const ColumnFilter = (props: IProps) => {
  const { search } = useLocation();
  const navigate = useNavigate();

  const { paramKey } = props;

  const title: string = useMemo(() => {
    switch (paramKey) {
      case 'status':
        return 'Status';
      default:
        return '';
    }
  }, [paramKey]);

  const options = useMemo(() => {
    switch (paramKey) {
      case 'status':
        return EStatusLinkManagement;
      default:
        return {};
    }
  }, [paramKey]);

  const filterMapper: Record<string, string> = useMemo(() => {
    switch (paramKey) {
      case 'status':
        return StatusLinkManagementValueMapper;
      default:
        return {};
    }
  }, [paramKey]);

  const [menuOpen, setMenuOpen] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState<Record<string, boolean>>(
    (Object.values(options) as string[]).reduce(
      (acc: Record<string, boolean>, item: string) => ({
        ...acc,
        [item]: false,
      }),
      {} as Record<string, boolean>,
    ),
  );

  useEffect(() => {
    const searchParams = new URLSearchParams(search);
    const paramValues = searchParams.getAll(paramKey);

    if (paramValues.some((key) => !selectedFilters[key])) {
      const newParamValues = { ...selectedFilters };

      paramValues.forEach((key) => {
        if (key in selectedFilters) {
          newParamValues[key] = true;
        }
      });

      setSelectedFilters(newParamValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  useEffect(() => {
    const searchParams = new URLSearchParams(search);
    const selectedParamValues = Object.entries(selectedFilters)
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      .filter(([_, isSelected]) => isSelected)
      .map(([key]) => key);

    if (selectedParamValues.length > 0) {
      searchParams.delete(paramKey);
      selectedParamValues.forEach((value) => searchParams.append(paramKey, value));
    } else {
      searchParams.delete(paramKey);
    }

    navigate(`?${searchParams.toString()}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFilters]);

  const toggleMenu = () => setMenuOpen(!menuOpen);

  const handleCheckboxChange = (key: string) => {
    setSelectedFilters((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleClickAway = () => setMenuOpen(false);

  const isAnySelected = useMemo(
    () => Object.values(selectedFilters).some((value) => value),
    [selectedFilters],
  );

  const onChangeCheckbox = (key: any) => () => handleCheckboxChange(key);

  return (
    <>
      <FilterAltIcon
        fontSize="small"
        sx={{
          color: isAnySelected ? '#FF4848' : '#44AEFB',
          cursor: 'pointer',
          transition: 'transform 0.2s ease-in-out',
          '&:hover': {
            transform: 'scale(1.2)',
          },
          '&:active': {
            transform: 'scale(0.95)',
          },
        }}
        onClick={toggleMenu}
      />
      {menuOpen && (
        <Dialog
          open={menuOpen}
          onClose={handleClickAway}
          PaperProps={{
            style: {
              padding: '0px',
              minWidth: '200px',
              maxHeight: '50%',
            },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '14px 16px',
            }}
          >
            <Typography
              sx={{
                fontSize: '12px',
                lineHeight: '20px',
                color: '#A3A7B0',
              }}
            >
              {title}
            </Typography>
            <ArrowDropDownIcon
              fontSize="small"
              sx={{ color: '#A3A7B0', cursor: 'pointer' }}
              onClick={handleClickAway}
            />
          </Box>
          {Object.keys(selectedFilters).map((key) => (
            <FormControlLabel
              key={key}
              sx={{
                padding: '14px 16px',
                margin: '0px',
                '& .MuiFormControlLabel-label': {
                  fontSize: '12px',
                  lineHeight: '20px',
                  color: '#A3A7B0',
                },
              }}
              control={
                <Checkbox
                  sx={{
                    padding: '0',
                    marginRight: '8px',
                  }}
                  checked={selectedFilters[key]}
                  onChange={onChangeCheckbox(key)}
                />
              }
              label={filterMapper[key]}
            />
          ))}
        </Dialog>
      )}
    </>
  );
};

export default ColumnFilter;
